import type {
  PlayerContract,
  SalaryCapState,
  ContractNegotiation,
  ContractOffer,
  ContractTrigger,
  EscalationClause,
  ContractHistoryEntry,
  GameState,
  Player,
} from './types'
import { deterministicDraw, stableHash } from './deterministic'

const seasonNumber = (state: Partial<Pick<GameState, 'season' | 'recruitment'>>) => state.season?.season ?? state.recruitment?.season ?? 1

// Default salary cap configuration
const DEFAULT_HARD_CAP = 13000 // $13M in thousands
const DEFAULT_DESIGNATED_SLOTS = 2

// Agent archetypes for negotiation variety
const AGENT_ARCHETYPES = {
  aggressive: { risk: 'high' as const, counterChance: 0.7, walkAwayChance: 0.3, demandMultiplier: 1.25 },
  balanced: { risk: 'medium' as const, counterChance: 0.5, walkAwayChance: 0.15, demandMultiplier: 1.1 },
  accommodating: { risk: 'low' as const, counterChance: 0.3, walkAwayChance: 0.05, demandMultiplier: 1.0 },
} as const

// Contract value tiers based on player quality
function estimatePlayerValue(player: Player): number {
  const ageFactor = player.age <= 23 ? 1.2 : player.age <= 26 ? 1.0 : player.age <= 29 ? 0.85 : 0.7
  const abilityScores = Object.values(player.abilities).map(gradeToScore)
  const avgAbility = abilityScores.reduce((a, b) => a + b, 0) / abilityScores.length
  const formFactor = player.form / 100
  const baseValue = 300 + avgAbility * 200 // $300k-$800k base
  return Math.round(baseValue * ageFactor * formFactor)
}

function gradeToScore(grade: string): number {
  switch (grade) {
    case 'Elite': return 5
    case 'Above Standard': return 4
    case 'Standard': return 3
    case 'Below Standard': return 2
    case 'Poor': return 1
    default: return 3
  }
}

function pickAgentArchetype(key: string): keyof typeof AGENT_ARCHETYPES {
  const rand = deterministicDraw(`${key}:agent-archetype`)
  if (rand < 0.25) return 'aggressive'
  if (rand < 0.7) return 'balanced'
  return 'accommodating'
}

// Create default contract for a player
export function createDefaultContract(player: Player, season: number): PlayerContract {
  const estimatedValue = estimatePlayerValue(player)
  const years = player.age <= 23 ? 3 : player.age <= 26 ? 2 : 1
  const baseSalary = Math.round(estimatedValue / years)

  const triggers: ContractTrigger[] = []
  if (player.age <= 24) {
    triggers.push({
      type: 'games-played',
      threshold: 15,
      bonus: Math.round(baseSalary * 0.1),
      description: 'Play 15+ senior games',
    })
  }
  if (player.development.seniorMatches >= 50) {
    triggers.push({
      type: 'development-milestone',
      threshold: 100,
      bonus: Math.round(baseSalary * 0.15),
      description: 'Reach 100 senior games',
    })
  }

  const escalationClauses: EscalationClause[] = [
    { type: 'annual', rate: 3, cap: Math.round(baseSalary * 1.5), description: '3% annual escalation' },
  ]

  return {
    playerId: player.id,
    yearsRemaining: years,
    baseSalary,
    totalValue: baseSalary * years,
    triggers,
    escalationClauses,
    status: 'active',
    signedSeason: season,
  }
}

// Initialize salary cap for a club
export function createDefaultSalaryCap(): SalaryCapState {
  return {
    hardCap: DEFAULT_HARD_CAP,
    designatedPlayerSlots: DEFAULT_DESIGNATED_SLOTS,
    designatedPlayers: [],
    capSpace: DEFAULT_HARD_CAP,
    committedCap: 0,
    deadCap: 0,
  }
}

// Recalculate salary cap from current contracts
export function recalculateSalaryCap(state: GameState): SalaryCapState {
  const contracts = state.club.contracts || {}
  let committed = 0
  let dead = 0

  for (const contract of Object.values(contracts)) {
    if (contract.status === 'active' || contract.status === 'expiring' || contract.status === 'negotiating') {
      committed += contract.baseSalary
    } else if (contract.status === 'delisted') {
      dead += contract.baseSalary
    }
  }

  const cap = state.club.salaryCap || createDefaultSalaryCap()
  return {
    ...cap,
    committedCap: committed,
    deadCap: dead,
    capSpace: cap.hardCap - committed - dead,
  }
}

// Hydrate contracts for all players (v51 migration)
export function hydrateContracts(state: GameState, existingContracts?: Record<string, PlayerContract>): Record<string, PlayerContract> {
  const contracts: Record<string, PlayerContract> = { ...existingContracts }

  for (const player of state.club.players) {
    if (!contracts[player.id]) {
      contracts[player.id] = createDefaultContract(player, seasonNumber(state))
    }
  }

  // Preserve live dead-cap obligations after a player leaves the roster.
  const playerIds = new Set(state.club.players.map(p => p.id))
  for (const id of Object.keys(contracts)) {
    if (!playerIds.has(id) && contracts[id].status !== 'delisted') {
      delete contracts[id]
    }
  }

  return contracts
}

// Migrate v51 saves and early v52 saves that stored contract data at the
// GameState root. ClubState is canonical, while the root aliases are kept in
// sync for backwards-compatible persistence.
export function hydrateContractState(state: GameState, saved: Partial<GameState> = state): GameState {
  const savedClub = saved.club
  const contracts = hydrateContracts(state, savedClub?.contracts ?? saved.contracts)
  const salaryCapTemplate = savedClub?.salaryCap ?? saved.salaryCap ?? createDefaultSalaryCap()
  const contractNegotiations = savedClub?.contractNegotiations ?? saved.contractNegotiations ?? []
  const contractHistory = savedClub?.contractHistory ?? saved.contractHistory ?? []
  const club = {
    ...state.club,
    contracts,
    salaryCap: salaryCapTemplate,
    contractNegotiations,
    contractHistory,
  }
  const salaryCap = recalculateSalaryCap({ ...state, club })

  return {
    ...state,
    club: { ...club, salaryCap },
    contracts,
    salaryCap,
    contractNegotiations,
    contractHistory,
  }
}

// Start a contract negotiation
export function startContractNegotiation(
  state: GameState,
  playerId: string,
  type: 'extension' | 'new' | 'restructure'
): GameState {
  const player = state.club.players.find(p => p.id === playerId)
  if (!player) return state

  const negotiations = state.club.contractNegotiations || []
  if (negotiations.some(negotiation => negotiation.playerId === playerId &&
    ['pending', 'offered', 'countered', 'accepted'].includes(negotiation.status))) return state

  const existingContract = state.club.contracts?.[playerId]
  const season = seasonNumber(state)
  const seed = state.season?.seed ?? state.seed
  const identity = `${seed}:s${season}:r${state.round}:player:${playerId}:type:${type}`
  const archetype = pickAgentArchetype(identity)
  const agent = AGENT_ARCHETYPES[archetype]

  // Club offer: reasonable extension based on current value
  const clubValue = existingContract ? existingContract.baseSalary : estimatePlayerValue(player)
  const clubOffer: ContractOffer = {
    years: type === 'extension' && existingContract ? existingContract.yearsRemaining + 2 : 2,
    baseSalary: Math.round(clubValue * (type === 'restructure' ? 0.9 : 1.0)),
    triggers: existingContract?.triggers || [],
    escalationClauses: existingContract?.escalationClauses || [{ type: 'annual', rate: 3, cap: Math.round(clubValue * 1.5), description: '3% annual escalation' }],
    totalValue: 0, // calculated below
    guaranteedYears: Math.min(2, type === 'extension' && existingContract ? existingContract.yearsRemaining + 1 : 2),
    signingBonus: Math.round(clubValue * 0.1),
  }
  clubOffer.totalValue = clubOffer.baseSalary * clubOffer.years + clubOffer.signingBonus

  // Agent demand: higher based on archetype
  const agentDemand: ContractOffer = {
    years: clubOffer.years + 1,
    baseSalary: Math.round(clubOffer.baseSalary * agent.demandMultiplier),
    triggers: [...clubOffer.triggers, { type: 'games-played', threshold: 10, bonus: Math.round(clubOffer.baseSalary * 0.15), description: 'Play 10+ games' }],
    escalationClauses: [...clubOffer.escalationClauses, { type: 'performance', rate: 5, description: 'Performance escalation' }],
    totalValue: 0,
    guaranteedYears: clubOffer.years,
    signingBonus: Math.round(clubOffer.baseSalary * agent.demandMultiplier * 0.2),
  }
  agentDemand.totalValue = agentDemand.baseSalary * agentDemand.years + agentDemand.signingBonus

  const negotiation: ContractNegotiation = {
    id: `neg-s${season}-r${state.round}-${playerId}-${stableHash(identity).toString(16)}`,
    playerId,
    type,
    status: 'pending',
    clubOffer,
    agentDemand,
    roundsRemaining: 3,
    agentRisk: archetype,
    createdRound: state.round,
    createdSeason: season,
  }

  return {
    ...state,
    club: {
      ...state.club,
      contracts: existingContract
        ? { ...state.club.contracts, [playerId]: { ...existingContract, status: 'negotiating' } }
        : state.club.contracts,
      contractNegotiations: [...negotiations, negotiation],
    },
  }
}

// Process negotiation for a round (agent responds)
export function processNegotiationRound(state: GameState): GameState {
  const negotiations = state.club.contractNegotiations || []
  if (negotiations.length === 0) return state

  const updated = negotiations.map(neg => {
    if (neg.status !== 'offered' && neg.status !== 'countered') return neg
    const transitionKey = `s${seasonNumber(state)}-r${state.round}`
    if (neg.lastProcessedTransition === transitionKey) return neg
    if (neg.roundsRemaining <= 0) {
      return { ...neg, status: 'walked-away' as const, lastProcessedTransition: transitionKey }
    }

    const agent = AGENT_ARCHETYPES[neg.agentRisk]
    const offerState = [
      neg.status,
      neg.roundsRemaining,
      neg.clubOffer.years,
      neg.clubOffer.baseSalary,
      neg.clubOffer.totalValue,
      neg.clubOffer.guaranteedYears,
      neg.clubOffer.signingBonus,
      neg.agentDemand.years,
      neg.agentDemand.baseSalary,
      neg.agentDemand.totalValue,
    ].join(':')
    const responseKey = `${state.season?.seed ?? state.seed}:${neg.id}:${transitionKey}:${offerState}`
    const willCounter = deterministicDraw(`${responseKey}:counter`) < agent.counterChance
    const willWalk = deterministicDraw(`${responseKey}:walk`) < agent.walkAwayChance

    if (willWalk) {
      return { ...neg, status: 'walked-away' as const, roundsRemaining: 0, lastProcessedTransition: transitionKey }
    }

    if (willCounter) {
      // Agent counters with slightly reduced demand
      const counteredDemand: ContractOffer = {
        ...neg.agentDemand,
        baseSalary: Math.round(neg.agentDemand.baseSalary * 0.95),
        years: neg.agentDemand.years,
        totalValue: Math.round(neg.agentDemand.baseSalary * 0.95 * neg.agentDemand.years + neg.agentDemand.signingBonus),
      }
      return {
        ...neg,
        status: 'countered' as const,
        agentDemand: counteredDemand,
        roundsRemaining: neg.roundsRemaining - 1,
        lastProcessedTransition: transitionKey,
      }
    }

    // Agent accepts club offer
    return { ...neg, status: 'accepted' as const, roundsRemaining: 0, lastProcessedTransition: transitionKey }
  })

  if (updated.every((negotiation, index) => negotiation === negotiations[index])) return state

  const walkedAwayPlayerIds = new Set(updated.filter(neg => neg.status === 'walked-away').map(neg => neg.playerId))
  const contracts = Object.fromEntries(Object.entries(state.club.contracts || {}).map(([playerId, contract]) => [
    playerId,
    walkedAwayPlayerIds.has(playerId) && contract.status === 'negotiating'
      ? { ...contract, status: contract.yearsRemaining <= 1 ? 'expiring' as const : 'active' as const }
      : contract,
  ]))

  return {
    ...state,
    club: { ...state.club, contracts, contractNegotiations: updated },
  }
}

// Accept negotiation (apply contract)
export function acceptNegotiation(state: GameState, negotiationId: string): GameState {
  const negotiations = state.club.contractNegotiations || []
  const neg = negotiations.find(n => n.id === negotiationId)
  if (!neg || neg.status !== 'accepted') return state

  const offer = neg.clubOffer
  const currentContract = state.club.contracts?.[neg.playerId]
  const cap = recalculateSalaryCap(state)
  const currentAnnualCost = currentContract && ['active', 'expiring', 'negotiating'].includes(currentContract.status)
    ? currentContract.baseSalary
    : 0
  if (cap.committedCap - currentAnnualCost + offer.baseSalary + cap.deadCap > cap.hardCap) return state
  const newContract: PlayerContract = {
    playerId: neg.playerId,
    yearsRemaining: offer.years,
    baseSalary: offer.baseSalary,
    totalValue: offer.totalValue,
    triggers: offer.triggers,
    escalationClauses: offer.escalationClauses,
    status: 'active',
    signedSeason: neg.createdSeason,
    lastNegotiatedRound: state.round,
  }

  const historyEntry: ContractHistoryEntry = {
    season: seasonNumber(state),
    round: state.round,
    type: neg.type === 'new' ? 'signed' : neg.type === 'extension' ? 'extended' : 'restructured',
    playerId: neg.playerId,
    playerName: state.club.players.find(p => p.id === neg.playerId)?.name || neg.playerId,
    details: `${offer.years} years, $${offer.baseSalary}k/yr, $${offer.signingBonus}k signing bonus`,
    capImpact: offer.baseSalary * offer.years,
  }

  return {
    ...state,
    club: {
      ...state.club,
      contracts: { ...state.club.contracts, [neg.playerId]: newContract },
      contractNegotiations: negotiations.filter(n => n.id !== negotiationId),
      contractHistory: [...(state.club.contractHistory || []), historyEntry],
      salaryCap: recalculateSalaryCap({
        ...state,
        club: { ...state.club, contracts: { ...state.club.contracts, [neg.playerId]: newContract } },
      }),
    },
  }
}

// Reject negotiation
export function rejectNegotiation(state: GameState, negotiationId: string): GameState {
  const negotiations = state.club.contractNegotiations || []
  const neg = negotiations.find(n => n.id === negotiationId)
  if (!neg) return state

  const contract = state.club.contracts?.[neg.playerId]
  const contracts = contract?.status === 'negotiating'
    ? { ...state.club.contracts, [neg.playerId]: { ...contract, status: contract.yearsRemaining <= 1 ? 'expiring' as const : 'active' as const } }
    : state.club.contracts

  const historyEntry: ContractHistoryEntry = {
    season: seasonNumber(state),
    round: state.round,
    type: 'restructured',
    playerId: neg.playerId,
    playerName: state.club.players.find(p => p.id === neg.playerId)?.name || neg.playerId,
    details: `Negotiation rejected by club`,
    capImpact: 0,
  }

  return {
    ...state,
    club: {
      ...state.club,
      contracts,
      contractNegotiations: negotiations.filter(n => n.id !== negotiationId),
      contractHistory: [...(state.club.contractHistory || []), historyEntry],
    },
  }
}

// Make counter-offer
export function counterOffer(state: GameState, negotiationId: string, offer: ContractOffer): GameState {
  const negotiations = state.club.contractNegotiations || []
  const neg = negotiations.find(n => n.id === negotiationId)
  if (!neg || (neg.status !== 'pending' && neg.status !== 'countered')) return state

  const updatedNeg: ContractNegotiation = {
    ...neg,
    clubOffer: offer,
    status: 'offered',
    roundsRemaining: Math.max(1, neg.roundsRemaining - 1),
  }

  return {
    ...state,
    club: {
      ...state.club,
      contractNegotiations: negotiations.map(n => n.id === negotiationId ? updatedNeg : n),
    },
  }
}

// Delist player (contract becomes dead cap)
export function delistPlayerContract(state: GameState, playerId: string): GameState {
  const contract = state.club.contracts?.[playerId]
  if (!contract || contract.status === 'delisted' || contract.status === 'unsigned') return state

  const updatedContract: PlayerContract = {
    ...contract,
    status: 'delisted',
  }

  const historyEntry: ContractHistoryEntry = {
    season: seasonNumber(state),
    round: state.round,
    type: 'delisted',
    playerId,
    playerName: state.club.players.find(p => p.id === playerId)?.name || playerId,
    details: `Delisted with ${contract.yearsRemaining} years, $${contract.baseSalary}k/yr remaining`,
    capImpact: contract.baseSalary * contract.yearsRemaining,
  }

  return {
    ...state,
    club: {
      ...state.club,
      contracts: { ...state.club.contracts, [playerId]: updatedContract },
      contractHistory: [...(state.club.contractHistory || []), historyEntry],
      salaryCap: recalculateSalaryCap({
        ...state,
        club: { ...state.club, contracts: { ...state.club.contracts, [playerId]: updatedContract } },
      }),
    },
  }
}

// Apply annual contract escalation (season rollover)
export function applyAnnualEscalation(state: GameState): GameState {
  const contracts = state.club.contracts || {}
  let changed = false
  const updatedContracts = { ...contracts }

  for (const [playerId, contract] of Object.entries(contracts)) {
    if (contract.status !== 'active' && contract.status !== 'expiring') continue

    let newSalary = contract.baseSalary
    for (const clause of contract.escalationClauses) {
      if (clause.type === 'annual') {
        newSalary = Math.round(newSalary * (1 + clause.rate / 100))
        if (clause.cap && newSalary > clause.cap) newSalary = clause.cap
      }
    }

    if (newSalary !== contract.baseSalary) {
      updatedContracts[playerId] = {
        ...contract,
        baseSalary: newSalary,
        totalValue: newSalary * contract.yearsRemaining,
      }
      changed = true
    }

    // Update status for expiring contracts
    if (contract.yearsRemaining === 1 && contract.status === 'active') {
      updatedContracts[playerId] = { ...updatedContracts[playerId], status: 'expiring' }
      changed = true
    }
  }

  if (!changed) return state

  return {
    ...state,
    club: {
      ...state.club,
      contracts: updatedContracts,
      salaryCap: recalculateSalaryCap({ ...state, club: { ...state.club, contracts: updatedContracts } }),
    },
  }
}

// Decrement contract years (end of season)
export function decrementContractYears(state: GameState): GameState {
  const contracts = state.club.contracts || {}
  let changed = false
  const updatedContracts = { ...contracts }
  const history: ContractHistoryEntry[] = []

  for (const [playerId, contract] of Object.entries(contracts)) {
    if (contract.status !== 'active' && contract.status !== 'expiring' && contract.status !== 'delisted') continue

    const newYears = contract.yearsRemaining - 1
    if (newYears <= 0) {
      // Contract expired
      updatedContracts[playerId] = { ...contract, status: 'unsigned', yearsRemaining: 0, totalValue: 0 }
      history.push({
        season: seasonNumber(state),
        round: 0,
        type: 'expired',
        playerId,
        playerName: state.club.players.find(p => p.id === playerId)?.name || playerId,
        details: 'Contract expired',
        capImpact: 0,
      })
      changed = true
    } else {
      updatedContracts[playerId] = {
        ...contract,
        yearsRemaining: newYears,
        totalValue: contract.baseSalary * newYears,
        status: contract.status === 'delisted' ? 'delisted' : newYears === 1 ? 'expiring' : 'active',
      }
      changed = true
    }
  }

  if (!changed) return state

  return {
    ...state,
    club: {
      ...state.club,
      contracts: updatedContracts,
      contractHistory: [...(state.club.contractHistory || []), ...history],
      salaryCap: recalculateSalaryCap({ ...state, club: { ...state.club, contracts: updatedContracts } }),
    },
  }
}

// Trigger contract bonuses based on performance
export function checkContractTriggers(state: GameState, playerId: string, triggerType: ContractTrigger['type'], value: number): GameState {
  const contract = state.club.contracts?.[playerId]
  if (!contract || contract.status !== 'active') return state

  const triggered = contract.triggers.filter(t => t.type === triggerType && value >= t.threshold)
  if (triggered.length === 0) return state

  // For now, just log the trigger - actual payment would be in cash flow
  const historyEntry: ContractHistoryEntry = {
    season: seasonNumber(state),
    round: state.round,
    type: 'restructured',
    playerId,
    playerName: state.club.players.find(p => p.id === playerId)?.name || playerId,
    details: `Triggered: ${triggered.map(t => t.description).join(', ')}`,
    capImpact: triggered.reduce((sum, t) => sum + t.bonus, 0),
  }

  return {
    ...state,
    club: {
      ...state.club,
      contractHistory: [...(state.club.contractHistory || []), historyEntry],
    },
  }
}

// Check if club can afford a contract offer (cap space)
export function canAffordContract(state: GameState, offer: ContractOffer): boolean {
  const cap = recalculateSalaryCap(state)
  const annualCost = offer.baseSalary
  return cap.capSpace >= annualCost
}

// Get contract summary for a player
export function getContractSummary(state: GameState, playerId: string): string | null {
  const contract = state.club.contracts?.[playerId]
  if (!contract) return null

  const statusLabel = contract.status === 'expiring' ? ' (EXPIRING)' : contract.status === 'negotiating' ? ' (NEGOTIATING)' : ''
  return `${contract.yearsRemaining}yr @ $${contract.baseSalary}k/yr${statusLabel}`
}

// Get all expiring contracts
export function getExpiringContracts(state: GameState): PlayerContract[] {
  return Object.values(state.club.contracts || {}).filter(c => c.status === 'expiring' || c.yearsRemaining <= 1)
}

// Get active negotiations
export function getActiveNegotiations(state: GameState): ContractNegotiation[] {
  return (state.club.contractNegotiations || []).filter(n =>
    n.status === 'pending' || n.status === 'offered' || n.status === 'countered'
  )
}